import React, { useEffect, useState, useRef, Fragment } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import DiscountCouponService from "../DiscountCoupon/Discountcoupons.service";
import AuthService from "../Authentication/auth.service";
import Alerts from "../../components/Alerts/alerts";
import { Tooltip } from "@mui/material";
import PermissionAccess from '../../PermissionAccess/Access.js';
import { openStoredFile, uploadFile, getDisplayFileName } from "../../utils/fileUtils";
import Swal from "sweetalert2";
import { useSensitiveAction } from "../../context/SensitiveInfoContext";

const TabDocument = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [doctorDocuments, setDoctorDocuments] = useState([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [existingDoctorDocumentId, setExistingDoctorDocumentId] = useState(null);
  const { authData } = PermissionAccess();
  const runSensitive = useSensitiveAction();

  const normalizeId = (value) => {
    if (value == null) return "";
    return value.toString ? value.toString() : String(value);
  };

  const fetchDocuments = () => {
    setLoading(true);
    Promise.all([
      DiscountCouponService.getDocumentsForDoctor(authData.sessionID, id),
      DiscountCouponService.getAllDoctorDocuments(authData.sessionID, id),
    ])
      .then(([typesResponse, docsResponse]) => {
        if (typesResponse.data.message === "Unauthorized" || docsResponse.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
          return;
        }

        const typesData = typesResponse.data?.data;
        let allTypes = [];

        if (typesData?.uploaded || typesData?.pending) {
          allTypes = [...(typesData.uploaded || []), ...(typesData.pending || [])];
        } else if (Array.isArray(typesData)) {
          allTypes = typesData;
        }

        setDocumentTypes(allTypes);
        setDoctorDocuments(Array.isArray(docsResponse.data?.data) ? docsResponse.data.data : []);
      })
      .catch((error) => {
        const errContent =
          (error.response && error.response.data) ||
          error.message ||
          error.toString();
        if (errContent) {
          Alerts.swalErrorAlert(errContent);
        } else {
          Alerts.swalErrorAlert(t("Server Error, Please try again later."));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchDocuments();
  }, [id]);

  const getDoctorDocumentForType = (docTypeId) => {
    const typeId = normalizeId(docTypeId);
    return doctorDocuments.find((doc) => {
      const docId = normalizeId(doc.document_id);
      return docId === typeId && doc.file && doc.upload_status === "uploaded";
    });
  };

  const getUploadDate = (document) => {
    const dateValue = document?.updated_at || document?.datetime_added;
    if (!dateValue) return t("Unknown date");
    return new Date(dateValue).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const handleUploadClick = (docTypeId, existingDoc) => {
    setSelectedDocumentId(docTypeId);
    setExistingDoctorDocumentId(existingDoc?._id || null);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file || !selectedDocumentId) return;

    const fileType = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")
      ? "pdf"
      : "image";

    setUploading(true);
    try {
      const filePath = await uploadFile(file, "image");
      const payload = {
        file: filePath,
        file_name: file.name,
        file_type: fileType,
        document_id: selectedDocumentId,
        doctor_id: id,
        document_action: "Pending",
      };

      const response = existingDoctorDocumentId
        ? await DiscountCouponService.UpdateDoctorDocument(authData.sessionID, {
          ...payload,
          doctorDocument_id: existingDoctorDocumentId,
        })
        : await DiscountCouponService.AddDoctorDocument(authData.sessionID, payload);

      if (response.data?.message === "Unauthorized") {
        AuthService.logout();
        navigate("/login");
        window.location.reload();
        return;
      }

      if (response.data?.status) {
        Swal.fire({
          icon: "success",
          title: t("Document Uploaded"),
          text: t("Document has been uploaded successfully"),
          confirmButtonColor: "#00BFA6",
        });
        fetchDocuments();
      } else {
        throw new Error(response.data?.message || t("Failed to upload document"));
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      Swal.fire({
        icon: "error",
        title: t("Upload Failed"),
        text: error.response?.data?.message || error.message || t("Failed to upload document"),
      });
    } finally {
      setUploading(false);
      setSelectedDocumentId(null);
      setExistingDoctorDocumentId(null);
    }
  };

  return (
    <Fragment>
      <LoadingOverlay
        tag="div"
        active={loading || uploading}
        styles={{ overlay: (base) => ({ ...base }) }}
        spinner={<DNALoader />}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0 fw-bold">
            <i className="pe-7s-file me-2 text-primary"></i>
            {t("Documents")}
          </h5>
        </div>

        <div>
          {documentTypes.length === 0 && !loading ? (
            <div className="text-muted fst-italic p-3 bg-light rounded border">
              {t("No document types configured for this doctor's specialization.")}
            </div>
          ) : (
            documentTypes.map((docType) => {
              const uploadedDoc = getDoctorDocumentForType(docType._id);
              const pendingDoc = doctorDocuments.find(
                (doc) => normalizeId(doc.document_id) === normalizeId(docType._id)
              );

              return (
                <div key={docType._id} className="mb-4">
                  <h6 className="fw-bold mb-3">
                    <i className="pe-7s-file me-2 text-info"></i>
                    {docType.title}
                  </h6>

                  {uploadedDoc ? (
                    <div className="d-flex align-items-center justify-content-between bg-light p-3 rounded border">
                      <div className="d-flex align-items-center">
                        <div className="me-3">
                          <i className="pe-7s-file text-primary fs-3"></i>
                        </div>
                        <div>
                          <p className="mb-1 fw-bold text-dark">
                            {getDisplayFileName(uploadedDoc, t("Document"))}
                          </p>
                          <p className="text-muted small mb-0">
                            {t("Uploaded on")}: {getUploadDate(uploadedDoc)}
                          </p>
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <Tooltip placement="top" title={t("View Document")} arrow>
                          <button
                            type="button"
                            onClick={() => runSensitive(() => openStoredFile(
                              uploadedDoc.file,
                              uploadedDoc.file_type === "pdf" ? "pdf" : "image"
                            ))}
                            className="btn btn-outline-primary btn-sm btn-hover-shine"
                          >
                            <i className="pe-7s-look"></i> {t("View")}
                          </button>
                        </Tooltip>
                        <Tooltip placement="top" title={t("Replace Document")} arrow>
                          <button
                            type="button"
                            onClick={() => handleUploadClick(docType._id, pendingDoc || uploadedDoc)}
                            className="btn btn-outline-warning btn-sm btn-hover-shine"
                          >
                            <i className="pe-7s-refresh"></i> {t("Replace")}
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center justify-content-between bg-light p-3 rounded border">
                      <div className="text-muted fst-italic">
                        {t("No")} {docType.title.toLowerCase()} {t("uploaded")}
                      </div>
                      <Tooltip placement="top" title={t("Upload Document")} arrow>
                        <button
                          type="button"
                          onClick={() => handleUploadClick(docType._id, pendingDoc)}
                          className="btn btn-success btn-sm btn-hover-shine"
                        >
                          <i className="pe-7s-upload"></i> {t("Upload")}
                        </button>
                      </Tooltip>
                    </div>
                  )}
                </div>
              );
            })
          )}

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*,.pdf"
            className="d-none"
          />
        </div>
      </LoadingOverlay>
    </Fragment>
  );
};

export default TabDocument;
